<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { Button, InputText } from "primevue";
import type { Membership } from "@/stores/modules/organisations";

// The organisations the signed-in user belongs to, the active one marked:
// choosing another makes it active, and a reference number joins one more.
// Both reload the page, as everything is loaded for the active organisation.
const store = useStore();
const { t } = useI18n();
const user = computed(() => store.getters["users/user"]);
const memberships = computed<Membership[]>(() => store.getters["organisations/memberships"]);
const reference = ref("");
const busy = ref<number | null>(null);
const error = ref("");

watch(
  () => [user.value?.account, user.value?.organisations],
  () => {
    if (user.value) store.dispatch("organisations/fetchMemberships", user.value).catch(() => {});
  },
  { immediate: true },
);

async function switchTo(account: number) {
  if (busy.value !== null || account === Number(user.value?.account)) return;
  busy.value = account;
  error.value = "";
  try {
    await store.dispatch("organisations/switchTo", { user: user.value, account });
  } catch (e: any) {
    error.value =
      e?.message === "unknown-reference" ? t("ReferenceDoesNotExist") : t("OrganisationSwitchFailed");
    busy.value = null;
  }
}

function join() {
  const account = Number(reference.value.trim());
  if (!Number.isInteger(account) || account <= 0) {
    error.value = t("ReferenceDoesNotExist");
    return;
  }
  switchTo(account);
}
</script>

<template>
  <div v-if="user" class="flex flex-col gap-2 w-full">
    <span class="text-slate-500 text-xs">{{ t("Organisation") }}:</span>
    <div class="flex flex-col gap-1">
      <Button
        v-for="membership in memberships"
        :key="membership.account"
        severity="secondary"
        :variant="membership.account === Number(user.account) ? undefined : 'outlined'"
        class="!justify-between"
        size="small"
        fluid
        :loading="busy === membership.account"
        :disabled="busy !== null && busy !== membership.account"
        @click="switchTo(membership.account)"
      >
        <span class="flex flex-col items-start min-w-0 text-left">
          <span class="truncate max-w-full">{{ membership.title || t("Organisation") }}</span>
          <!-- The reference, which others join it by. -->
          <span class="text-[11px] leading-tight opacity-60">{{ t("Reference") }} {{ membership.account }}</span>
        </span>
        <i v-if="membership.account === Number(user.account)" class="pi pi-check text-xs" />
      </Button>
    </div>
    <form class="flex flex-col gap-1" @submit.prevent="join">
      <span class="text-xs text-slate-500">{{ t("OrganisationJoin") }}</span>
      <div class="flex gap-1">
        <InputText
          v-model="reference"
          inputmode="numeric"
          size="small"
          class="flex-1 min-w-0"
          :placeholder="t('Reference')"
          :disabled="busy !== null"
        />
        <Button type="submit" size="small" :label="t('Join')" :disabled="!reference.trim() || busy !== null" />
      </div>
      <span class="text-[11px] leading-tight text-slate-400 max-w-56">{{ t("OrganisationJoinHint") }}</span>
    </form>
    <span v-if="error" class="text-xs text-red-600">{{ error }}</span>
  </div>
</template>
